// @ts-strict-ignore
import Space from 'antd/lib/space';
import * as React from 'react';
import { FeatureSection } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import Icon, {
    AlignLeftOutlined,
    BarChartOutlined,
    BarsOutlined,
    BranchesOutlined,
    BugOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    ClockCircleFilled,
    ClockCircleOutlined,
    ControlOutlined,
    DashboardOutlined,
    FieldBinaryOutlined,
    FileTextOutlined,
    FlagOutlined,
    HeartOutlined,
    HistoryOutlined,
    InboxOutlined,
    LineChartOutlined,
    MessageOutlined,
    PictureOutlined,
    RiseOutlined,
    RocketOutlined,
    SearchOutlined,
    SettingOutlined,
    StarOutlined,
    TabletFilled,
    TabletOutlined,
    TagOutlined,
    TagsOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    ToolOutlined,
    TranslationOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { runwayColors } from '../styles/globalStyles';
import Divider from 'antd/lib/divider';
import pilotWings from '../../../assets/release-pilot-wings-white.svg';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectJoinedCommsProvidersNamesList,
    selectIsWorkflowsCIStepEnabled,
} from '../../selectors/selectors';
import { AppPlatform, AppStoreType, StatisticScope } from '../../types';
import { getIsAppleAppStore, getIsApplePlatform } from '../../helpers';
import { displayCIText } from '../../helpers/displayHelpers';

export const descriptionForFeatureSectionAndScope = (featureSection: FeatureSection, scope: StatisticScope) => {
    let scopeMessage = '';

    switch (scope) {
        case StatisticScope.App:
            scopeMessage = `the selected app`;
            break;
        case StatisticScope.AppGroup:
            scopeMessage = `the selected app group`;
            break;
        case StatisticScope.Org:
            scopeMessage = `your organization`;
            break;
    }
    switch (featureSection) {
        case FeatureSection.OrgOverviewWorkMetrics:
            return (
                <List>
                    <li>{`View key work-related metrics across recent releases for all apps in ${scopeMessage}, like average release duration, items of work completed, and review times.`}</li>
                </List>
            );
        case FeatureSection.OrgOverviewBuildMetrics:
            return (
                <List>
                    <li>{`View key build-related metrics across recent releases for all apps in ${scopeMessage}, like average build time and binary sizes.`}</li>
                </List>
            );
        case FeatureSection.OrgOverviewStabilityHealthMetrics:
            return (
                <List>
                    <li>{`View key stability and health metrics across recent releases for all apps in ${scopeMessage}, like average crash-free rates, user ratings, and hotfix counts.`}</li>
                </List>
            );
        case FeatureSection.OrgOverviewMobileDevOpsMetrics:
            return (
                <List>
                    <li>{`View Mobile DevOps metrics based on recent releases from ${scopeMessage}. Mobile DevOps metrics include release frequency, release failure rate, time to recovery, average release step completion times, and more!`}</li>
                </List>
            );
        case FeatureSection.OrgOverviewTriageMetrics:
            return (
                <List>
                    <li>{`View key triage metrics for all apps in ${scopeMessage}, like the number of issues detected and the average time from detection to fix merged.`}</li>
                </List>
            );
    }

    return null;
};

export const iconForFeatureSection = (featureSection: FeatureSection, platform: AppPlatform) => {
    switch (featureSection) {
        case FeatureSection.KickoffReleasePilot:
            return FileTextOutlined;
        case FeatureSection.KickoffReleaseDescription:
            return FileTextOutlined;
        case FeatureSection.KickoffFeatureAffiliation:
            return TagsOutlined;
        case FeatureSection.KickoffReleaseBranch:
            return BranchesOutlined;
        case FeatureSection.KickoffVersionInCode:
            return FieldBinaryOutlined;
        case FeatureSection.KickoffPreviousReleaseTag:
            return TagOutlined;
        case FeatureSection.HistoryTimeline:
            return ClockCircleOutlined;
        case FeatureSection.AutomationTab:
            return ThunderboltOutlined;
        case FeatureSection.ChecklistItems:
        case FeatureSection.ChecklistTab:
            return UnorderedListOutlined;
        case FeatureSection.KickoffCTAs:
            return CheckCircleOutlined;
        case FeatureSection.FeatureReadinessWorkItems:
            return TagsOutlined;
        case FeatureSection.TranslationStrings:
            return TranslationOutlined;
        case FeatureSection.TranslationCTAs:
            return CheckCircleOutlined;
        case FeatureSection.ReleaseCandidateActiveBuild:
            return ToolOutlined;
        case FeatureSection.ReleaseCandidateBuildHistory:
            return HistoryOutlined;
        case FeatureSection.RegressionTestingStatusPicker:
            return CheckCircleOutlined;
        case FeatureSection.RegressionTestingTestItems:
            return CheckOutlined;
        case FeatureSection.BetaTestingActiveBuild:
            return TabletFilled;
        case FeatureSection.BetaTestingBetaSoak:
            return ClockCircleFilled;
        case FeatureSection.BetaTestingWhatToTest:
            return FileTextOutlined;
        case FeatureSection.BetaTestingBetaGroups:
            return TeamOutlined;
        case FeatureSection.BetaTestingFeedback:
            return MessageOutlined;
        case FeatureSection.BetaTestingStabilityModule:
            return BugOutlined;
        case FeatureSection.BetaTestingBetaBuildHistory:
            return HistoryOutlined;
        case FeatureSection.ScreenshotsTabsContent:
            return PictureOutlined;
        case FeatureSection.MetadataReleaseNotesContent:
            return FileTextOutlined;
        case FeatureSection.MetadataKeywordsOrShortDescription:
            return getIsApplePlatform(platform) ? TagsOutlined : AlignLeftOutlined;
        case FeatureSection.MetadataAppName:
            return FileTextOutlined;
        case FeatureSection.MetadataDescription:
            return AlignLeftOutlined;
        case FeatureSection.MetadataSubtitle:
            return FileTextOutlined;
        case FeatureSection.MetadataPromotionalText:
            return AlignLeftOutlined;
        case FeatureSection.ApprovalsItemList:
            return CheckOutlined;
        case FeatureSection.SubmissionSelectedBuildModule:
            return TabletOutlined;
        case FeatureSection.SubmissionAppStoreReleaseSettings:
            return SettingOutlined;
        case FeatureSection.ReviewAppStoreReview:
            return SearchOutlined;
        case FeatureSection.ReleaseReleasedBuild:
            return RocketOutlined;
        case FeatureSection.ReleasePhasedReleaseInfo:
            return RiseOutlined;
        case FeatureSection.ReleaseAutomatedReleaseTasks:
            return ThunderboltOutlined;
        case FeatureSection.OverviewTopOverview:
            return CalendarOutlined;
        case FeatureSection.OverviewProduct:
            return BarsOutlined;
        case FeatureSection.OverviewCode:
            return BarChartOutlined;
        case FeatureSection.RolloutTopSummary:
            return ControlOutlined;
        case FeatureSection.RolloutPhasedReleaseModule:
            return RiseOutlined;
        case FeatureSection.RolloutStabilityModule:
            return BugOutlined;
        case FeatureSection.RolloutObservabilityAndAnalyticsModule:
            return LineChartOutlined;
        case FeatureSection.RolloutUserReviewsModule:
            return StarOutlined;
        case FeatureSection.FeatureFlagsTable:
            return FlagOutlined;
        case FeatureSection.OrgOverviewWorkMetrics:
            return TeamOutlined;
        case FeatureSection.OrgOverviewBuildMetrics:
            return ToolOutlined;
        case FeatureSection.OrgOverviewStabilityHealthMetrics:
            return HeartOutlined;
        case FeatureSection.OrgOverviewMobileDevOpsMetrics:
            return DashboardOutlined;
        case FeatureSection.OrgOverviewTriageMetrics:
        case FeatureSection.Triage:
            return InboxOutlined;
    }
};

export const titleForFeatureSection = (featureSection: FeatureSection, appStoreType: AppStoreType) => {
    switch (featureSection) {
        case FeatureSection.KickoffReleasePilot:
            return 'Release pilot';
        case FeatureSection.KickoffReleaseDescription:
            return 'Release description';
        case FeatureSection.KickoffFeatureAffiliation:
            return 'Feature affiliation';
        case FeatureSection.KickoffReleaseBranch:
            return 'Release branch';
        case FeatureSection.KickoffVersionInCode:
            return 'Version in code';
        case FeatureSection.KickoffPreviousReleaseTag:
            return 'Previous release tag';
        case FeatureSection.HistoryTimeline:
            return 'History timeline';
        case FeatureSection.AutomationTab:
            return 'Automations';
        case FeatureSection.ChecklistTab:
            return 'Checklists';
        case FeatureSection.ChecklistItems:
            return 'Checklist items';
        case FeatureSection.KickoffCTAs:
            return 'Kickoff Actions';
        case FeatureSection.FeatureReadinessWorkItems:
            return 'Release work items';
        case FeatureSection.TranslationStrings:
            return 'Localizable strings';
        case FeatureSection.TranslationCTAs:
            return 'Translations step actions';
        case FeatureSection.ReleaseCandidateActiveBuild:
            return 'Active build';
        case FeatureSection.ReleaseCandidateBuildHistory:
            return 'Build history';
        case FeatureSection.RegressionTestingStatusPicker:
            return 'Regression testing status selection';
        case FeatureSection.RegressionTestingTestItems:
            return 'Regression test items';
        case FeatureSection.BetaTestingActiveBuild:
            return 'Active beta build';
        case FeatureSection.BetaTestingBetaSoak:
            return 'Beta soak period';
        case FeatureSection.BetaTestingWhatToTest:
            return 'Tester notes';
        case FeatureSection.BetaTestingBetaGroups:
            return 'Beta testing groups';
        case FeatureSection.BetaTestingFeedback:
            return 'Tester feedback';
        case FeatureSection.BetaTestingStabilityModule:
            return 'Stability info';
        case FeatureSection.BetaTestingBetaBuildHistory:
            return 'Beta build history';
        case FeatureSection.ScreenshotsTabsContent:
            return 'Screenshots for locale';
        case FeatureSection.MetadataReleaseNotesContent:
            return getIsAppleAppStore(appStoreType) ? `What's New notes` : `Release notes`;
        case FeatureSection.MetadataKeywordsOrShortDescription:
            return getIsAppleAppStore(appStoreType) ? `App keywords` : `Short description`;
        case FeatureSection.MetadataDescription:
            return 'Description';
        case FeatureSection.MetadataSubtitle:
            return 'Subtitle';
        case FeatureSection.MetadataAppName:
            return 'App name';
        case FeatureSection.MetadataPromotionalText:
            return 'Promotional text';
        case FeatureSection.ApprovalsItemList:
            return 'Approval Items';
        case FeatureSection.SubmissionSelectedBuildModule:
            return 'Selected build';
        case FeatureSection.SubmissionAppStoreReleaseSettings:
            return 'Release settings';
        case FeatureSection.ReviewAppStoreReview:
            return 'App store review';
        case FeatureSection.ReleasePhasedReleaseInfo:
            return 'Phased release info';
        case FeatureSection.ReleaseReleasedBuild:
            return 'Released build';
        case FeatureSection.ReleaseAutomatedReleaseTasks:
            return 'Automated post-release tasks';
        case FeatureSection.OverviewTopOverview:
            return 'Release details';
        case FeatureSection.OverviewProduct:
            return 'Product statistics';
        case FeatureSection.OverviewCode:
            return 'Build & code statistics';
        case FeatureSection.RolloutTopSummary:
            return 'Rollout summary';
        case FeatureSection.RolloutPhasedReleaseModule:
            return 'Phased release';
        case FeatureSection.RolloutStabilityModule:
            return 'Stability';
        case FeatureSection.RolloutObservabilityAndAnalyticsModule:
            return 'Observability & analytics';
        case FeatureSection.RolloutUserReviewsModule:
            return 'User reviews';
        case FeatureSection.FeatureFlagsTable:
            return 'Feature flags';
        case FeatureSection.OrgOverviewWorkMetrics:
            return 'Work metrics';
        case FeatureSection.OrgOverviewBuildMetrics:
            return 'Build metrics';
        case FeatureSection.OrgOverviewStabilityHealthMetrics:
            return 'Stability & health metrics';
        case FeatureSection.OrgOverviewMobileDevOpsMetrics:
            return 'Mobile DevOps metrics';
        case FeatureSection.OrgOverviewTriageMetrics:
            return 'Triage metrics';
        case FeatureSection.Triage:
            return 'Triage';
    }
};

const List: React.FC<React.PropsWithChildren> = ({ children }) => (
    <ul
        style={{
            color: runwayColors.white,
            listStylePosition: 'outside',
            paddingLeft: 0,
            display: 'flex',
            flexDirection: 'column',
            marginBottom: 0,
            gap: 8,
        }}
    >
        {children}
    </ul>
);

export const contentForFeatureSection = (
    featureSection: FeatureSection,
    appStoreType: AppStoreType,
    scope: StatisticScope,
    commsProviders: string,
    isWorkflows: boolean,
) => {
    switch (featureSection) {
        case FeatureSection.KickoffReleasePilot:
            return (
                <List>
                    <li>See who’s currently assigned to be the point person for this version release.</li>
                    <li>
                        Release pilots have elevated permissions and get special notifications during their releases.
                    </li>
                </List>
            );
        case FeatureSection.KickoffReleaseDescription:
            return (
                <List>
                    <li>
                        Add context here about this release that might be helpful to your internal team, like “Giant
                        refactor of checkout”, or “Support for holiday initiatives”
                    </li>
                    <li>These notes are only visible to team members in Runway.</li>
                </List>
            );
        case FeatureSection.KickoffFeatureAffiliation:
            return (
                <List>
                    <li>
                        Labels / fix versions associating items of work with this particular release. Configurable from
                        Settings {'>'} Integrations.
                    </li>
                </List>
            );
        case FeatureSection.KickoffReleaseBranch:
            return (
                <List>
                    <li>The branch that your release will be built from.</li>
                </List>
            );
        case FeatureSection.KickoffVersionInCode:
            return (
                <List>
                    <li>The version number currently in code.</li>
                </List>
            );
        case FeatureSection.KickoffPreviousReleaseTag:
            return (
                <List>
                    <li>
                        The release tag associated with your previous release. Runway will consider all work past this
                        tag to be associated with your next release.
                    </li>
                </List>
            );
        case FeatureSection.HistoryTimeline:
            return (
                <List>
                    <li>
                        Recent activity pertaining to this step. Click on View full timeline to see a full list or
                        activity from all steps.
                    </li>
                </List>
            );
        case FeatureSection.AutomationTab:
            return (
                <List>
                    <li>Status and details around automations associated with the current step.</li>
                </List>
            );
        case FeatureSection.ChecklistTab:
            return (
                <List>
                    <li>
                        {`You can add checklist items to any step in Runway to cover any unique parts of your team's
                        release process. A step won't be marked as complete (green) in Runway until all checklist items
                        have been completed (in addition to any other activity typically required for step completion).`}
                    </li>
                    <li>
                        When a checklist item is marked as completed, Runway will keep track of when the status changed,
                        and who updated it.
                    </li>
                </List>
            );
        case FeatureSection.ChecklistItems:
            return (
                <List>
                    <li>
                        {`Add checklist items to any step in Runway to cover any unique parts of your team's
                        release process.`}
                    </li>
                    <li>
                        Checklist item descriptions can include markdown, and they can assigned to specific user groups
                        or individual owners.
                    </li>
                    <li>
                        A step won&apos;t be marked as complete (green) in Runway until all checklist items have been
                        completed (in addition to any other activity typically required for step completion).
                    </li>
                    <li>
                        When a checklist item is marked as completed, Runway will keep track of when the status changed,
                        and who updated it.
                    </li>
                </List>
            );
        case FeatureSection.KickoffCTAs:
            return (
                <List>
                    <li>
                        You can manually initiate common release cycle tasks from here, such as bumping version in code,
                        promoting code from a development branch to master, or creating a release branch. All of these
                        tasks can be automated on a schedule, as well!
                    </li>
                </List>
            );
        case FeatureSection.TranslationStrings:
            return (
                <List>
                    <li>
                        {`View your code's localizable strings alongside each string's status and associated translations.`}
                    </li>
                    <li>
                        {`Each string's status will show you at a glance whether your code’s translations are in sync with
                        your provider, or whether they need to be updated.`}
                    </li>
                </List>
            );
        case FeatureSection.TranslationCTAs:
            return (
                <List>
                    <li>
                        You can manually trigger common translation string actions, like uploading the latest
                        localizable string source files to your translations provider or exporting updated localizable
                        string files back to your codebase. You can set up these tasks to happen automatically as well!
                    </li>
                </List>
            );
        case FeatureSection.FeatureReadinessWorkItems:
            return (
                <List>
                    <li>
                        View a complete list of tickets and code going into this release, including items that
                        haven&apos;t yet been completed.
                    </li>
                </List>
            );
        case FeatureSection.ReleaseCandidateActiveBuild:
            return (
                <List>
                    <li>
                        {`See the active ${displayCIText(isWorkflows, 'lowercase')} build and its associated status from your CI/CD integration. The active ${displayCIText(isWorkflows, 'uppercase')} will be the most recent build by default, but you can also manually lock an older build as the active ${displayCIText(isWorkflows, 'uppercase')}. You can also access any artifacts generated from the build from here.`}
                    </li>
                </List>
            );
        case FeatureSection.ReleaseCandidateBuildHistory:
            return (
                <List>
                    <li>{`View a list of past RC builds and their status. You can also choose an older RC build to lock as the active build from here`}</li>
                </List>
            );
        case FeatureSection.RegressionTestingStatusPicker:
            return (
                <List>
                    <li>
                        {`You can update the status of regression testing (and mark the appropriate RC build) from here.`}
                    </li>
                </List>
            );
        case FeatureSection.RegressionTestingTestItems:
            return (
                <List>
                    <li>
                        {`Add regression testing items and update their status from here. Regression items can be assigned to users with particular user groups (like QA), or to individual users. You can comment on specific regression items, or remind their owners to complete items on ${commsProviders} with the "Ping" button.`}
                    </li>
                </List>
            );
        case FeatureSection.BetaTestingActiveBuild:
            return (
                <List>
                    <li>{`The active build being beta tested, and it's relevant details including it's current testing status and its associated CI/CD build.${
                        getIsAppleAppStore(appStoreType) ?
                            ` You can also manually submit your Testflight build for beta review if needed.`
                        :   ''
                    }`}</li>
                </List>
            );
        case FeatureSection.BetaTestingBetaSoak:
            return (
                <List>
                    <li>{`See the status of your beta soak period based on your team's configured beta soak settings.`}</li>
                </List>
            );
        case FeatureSection.BetaTestingWhatToTest:
            return (
                <List>
                    <li>{`View and update the tester notes for this build`}</li>
                </List>
            );
        case FeatureSection.BetaTestingBetaGroups:
            return (
                <List>
                    <li>{`View and update the groups that have access to test your beta build`}</li>
                </List>
            );
        case FeatureSection.BetaTestingFeedback:
            return (
                <List>
                    <li>{`View feedback from TestFlight beta testers submitted for this build, including screenshots.`}</li>
                </List>
            );
        case FeatureSection.BetaTestingStabilityModule:
            return (
                <List>
                    <li>{`See the latest stability info on your beta build, including crash-free rates, adoption, and top issues.`}</li>
                </List>
            );
        case FeatureSection.BetaTestingBetaBuildHistory:
            return (
                <List>
                    <li>{`View a list of past beta builds for this release, including associated details like tester notes, groups, and stability info.`}</li>
                </List>
            );
        case FeatureSection.ScreenshotsTabsContent:
            return (
                <List>
                    <li>{`View and edit screenshots for each locale and device type supported by your app. Approve screenshots to mark the step as Ready.`}</li>
                </List>
            );
        case FeatureSection.MetadataReleaseNotesContent:
            return (
                <List>
                    <li>
                        {`View and edit ${
                            getIsAppleAppStore(appStoreType) ? `What's New` : `Release Notes`
                        } for this version for all locales supported by your app. If you've saved default release notes for your app, you can apply them with just one click from here, or enable the automation for Runway to automatically apply your default notes for each release.`}
                    </li>
                </List>
            );
        case FeatureSection.MetadataSubtitle:
            return (
                <List>
                    <li>{`View and edit your app's subtitle for all supported locales.`}</li>
                </List>
            );
        case FeatureSection.MetadataDescription:
            return (
                <List>
                    <li>{`View and edit your app's description for all supported locales.`}</li>
                </List>
            );
        case FeatureSection.MetadataAppName:
            return (
                <List>
                    <li>{`View and edit your app's name for all supported locales.`}</li>
                </List>
            );
        case FeatureSection.MetadataPromotionalText:
            return (
                <List>
                    <li>{`View and edit your app's promotional text for all supported locales.`}</li>
                </List>
            );
        case FeatureSection.MetadataKeywordsOrShortDescription:
            return (
                <List>
                    <li>
                        {getIsAppleAppStore(appStoreType) ?
                            `View and edit your app's keywords for all supported locales.`
                        :   `View and edit your app's short description for all supported locales.`}
                    </li>
                </List>
            );
        case FeatureSection.ApprovalsItemList:
            return (
                <List>
                    <li>
                        {`Add stakeholder approval items and update their status from here. Approval items can be assigned to all users that have a certain user group, or to individual users. You can comment on specific items, as well as remind owners via ${commsProviders} with the "Ping" button.`}
                    </li>
                </List>
            );
        case FeatureSection.SubmissionSelectedBuildModule:
            return (
                <List>
                    <li>{`View and update the build you've selected to submit for review. You can choose a build to submit for review from a list of build found in ${
                        getIsAppleAppStore(appStoreType) ? `TestFlight` : `Play Console`
                    }, or you can enable Runway to automatically select the most recent build.`}</li>
                </List>
            );
        case FeatureSection.SubmissionAppStoreReleaseSettings:
            return (
                <List>
                    <li>
                        {`View and update the release settings for this version.`}
                        <br />
                        <br />
                        {`${
                            getIsAppleAppStore(appStoreType) ?
                                `Manual or automatic release, as well as phased release settings can be found here. You can also view App Review details from here.`
                            :   `You can enable staged rollout, and configure an automated rollout sequence from here.`
                        }`}
                    </li>
                </List>
            );

        case FeatureSection.ReviewAppStoreReview:
            return (
                <List>
                    <li>{`View the details of your submitted build, and the most up to date status of your app store submission. You can also view review information from here.`}</li>
                </List>
            );
        case FeatureSection.ReleaseReleasedBuild:
            return (
                <List>
                    <li>{`View the build that was released to users for this version. You can also see stability monitoring info for the released build, like crash-rate, adoption, and top issues.`}</li>
                </List>
            );
        case FeatureSection.ReleasePhasedReleaseInfo:
            return (
                <List>
                    <li>{`See the status of your phased release. You can also ${
                        getIsAppleAppStore(appStoreType) ? `pause` : `halt`
                    } or accelerate your phased rollout to all users from here.`}</li>
                </List>
            );
        case FeatureSection.ReleaseAutomatedReleaseTasks:
            return (
                <List>
                    <li>{`See the status of any automated post-release tasks, like tagging the final commit in your VCS system.`}</li>
                </List>
            );
        case FeatureSection.OverviewTopOverview:
            return (
                <List>
                    <li>{`The key dates for your release: target kickoff, submission, and release.`}</li>
                </List>
            );
        case FeatureSection.OverviewProduct:
            return (
                <List>
                    <li>
                        {`View product-related statistics for the release, like items of work completed and review times.`}
                    </li>
                </List>
            );
        case FeatureSection.OverviewCode:
            return (
                <List>
                    <li>
                        {`View build and code related statistics for the release, like code contributors, lines of code changed, build workflow success rate, and more.`}
                    </li>
                </List>
            );
        case FeatureSection.RolloutTopSummary:
            return (
                <List>
                    <li>{`View a summary of your release rollout – phased release status, adoption, and current values for key metrics including stability, observability & analytics, and user reviews`}</li>
                </List>
            );
        case FeatureSection.RolloutPhasedReleaseModule:
            return (
                <List>
                    <li>
                        {`View details of your phased rollout – current phased rollout percentage, and the status of any rollout automations. You can also configure rollout automations from here including:`}
                        <List>
                            <li>{`Release stable phased releases to 100% of users`}</li>
                            <li>{`Halt unstable phased releases`}</li>
                        </List>
                    </li>
                </List>
            );
        case FeatureSection.RolloutStabilityModule:
            return (
                <List>
                    <li>{`See how your release's stability (crash-free users and crash-free sessions) has tracked over time since the rollout started. You can also view top stability issues from here.`}</li>
                </List>
            );
        case FeatureSection.RolloutObservabilityAndAnalyticsModule:
            return (
                <List>
                    <li>{`See how your app's key observability & analytics metrics  have tracked over time since the release rollout started. `}</li>
                </List>
            );
        case FeatureSection.RolloutUserReviewsModule:
            return (
                <List>
                    <li>{`See how your app's user reviews rating has tracked over time since the release rollout started. You can also see the most recent reviews for release version from here.`}</li>
                </List>
            );
        case FeatureSection.FeatureFlagsTable:
            return (
                <List>
                    <li>{`View feature flags and their associated details.`}</li>
                </List>
            );
        case FeatureSection.OrgOverviewWorkMetrics:
        case FeatureSection.OrgOverviewBuildMetrics:
        case FeatureSection.OrgOverviewStabilityHealthMetrics:
        case FeatureSection.OrgOverviewMobileDevOpsMetrics:
        case FeatureSection.OrgOverviewTriageMetrics:
            return descriptionForFeatureSectionAndScope(featureSection, scope);
        case FeatureSection.Triage:
            return (
                <List>
                    <li>{`Collect crashes, reviews, and other issues in one place, and triage them into fix requests routed to the right release.`}</li>
                </List>
            );
    }
};

export interface InfoViewProps {
    featureSections: FeatureSection[];
    appId: string;
    extraInfo?: {
        scope?: StatisticScope;
    };
}

export const InfoView: React.FC<InfoViewProps> = ({ featureSections, appId, extraInfo }) => {
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));
    return (
        <Space
            size={0}
            direction="vertical"
            split={
                <Divider
                    type="horizontal"
                    style={{
                        opacity: 0.5,
                        backgroundColor: runwayColors.white,
                    }}
                />
            }
        >
            {featureSections.map((featureSection) => (
                <div
                    key={featureSection}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '130px auto',
                        gridTemplateRows: 'auto auto',
                        columnGap: 24,
                    }}
                >
                    <Space direction="vertical">
                        <Text
                            style={{
                                fontSize: 16,
                                color: runwayColors.white,
                            }}
                        >
                            {titleForFeatureSection(featureSection, appStoreType)}
                        </Text>
                        {featureSection === FeatureSection.KickoffReleasePilot ?
                            <img src={pilotWings} width={38} height={38} />
                        :   <Icon
                                component={iconForFeatureSection(featureSection, platform)}
                                style={{
                                    fontSize: 24,
                                    color: runwayColors.white,
                                }}
                            />
                        }
                    </Space>
                    {contentForFeatureSection(
                        featureSection,
                        appStoreType,
                        extraInfo?.scope,
                        commsProviders,
                        isWorkflowsCIStepEnabled,
                    )}
                </div>
            ))}
        </Space>
    );
};
